import { ReactNode } from "react";
import { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description?: string;
  action?: ReactNode;
  secondaryAction?: ReactNode;
  className?: string;
  /** Visual size — default 'md' */
  size?: "sm" | "md" | "lg";
}

/**
 * Standardized empty state for tables, lists and dashboards.
 * Reuses brand colors via semantic tokens (primary tint background on the icon).
 */
export const EmptyState = ({
  icon: Icon,
  title,
  description,
  action,
  secondaryAction,
  className,
  size = "md",
}: EmptyStateProps) => {
  const padding = size === "sm" ? "py-10 px-4" : size === "lg" ? "py-24 px-6" : "py-16 px-6";
  const iconBox = size === "sm" ? "p-3 mb-3" : "p-4 mb-4";
  const iconSize = size === "sm" ? "h-7 w-7" : "h-10 w-10";
  const titleSize = size === "sm" ? "text-base" : "text-lg";

  return (
    <div className={cn("flex flex-col items-center justify-center text-center", padding, className)}>
      <div className={cn("rounded-full bg-primary/10 ring-1 ring-primary/20", iconBox)}>
        <Icon className={cn("text-primary", iconSize)} aria-hidden="true" />
      </div>
      <h3 className={cn("font-semibold text-foreground mb-1", titleSize)}>{title}</h3>
      {description && (
        <p className="text-muted-foreground text-sm mb-5 max-w-md">{description}</p>
      )}
      {(action || secondaryAction) && (
        <div className="flex flex-wrap items-center justify-center gap-2">
          {action}
          {secondaryAction}
        </div>
      )}
    </div>
  );
};

export default EmptyState;
